import { PayloadAction } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { SagaIterator } from 'redux-saga';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { selectBaseApiUrl } from '../../../core/config-loader/store/config-loader-slice';
import { downloadBinaryData } from '../../../shared/utils/download-binary-data.util';
import { LeadsysPictureDto } from '../../http/dto/leadsys-picture-dto.interface';
import {
  downloadLeadsysPictures,
  fetchLeadsysPictureFilters,
  fetchLeadsysPictures,
  fetchSurveyFilters,
} from '../../http/leadsys-picture-api';
import { LeadsysPictureFilterFormValue } from '../../interfaces/leadsys-picture-filter-form-value.interface';
import { LeadsysPictureFilterItem } from '../../interfaces/leadsys-picture-filter-item.interface';
import { LeadsysPictureFilters } from '../../interfaces/leadsys-picture-filters.interface';
import { LeadsysPicture } from '../../interfaces/leadsys-picture.interface';
import { mapLeadsysPictureDtosToLeadsysPictures } from '../../mappers/leadsys-picture-dtos-to-leadsys-pictures.mapper';
import { mapLeadsysPictureFiltersToUrlParams } from '../../mappers/leadsys-picture-filters-to-url-params.mapper';
import { leadsysPictureActions } from '../leadsysPictureSlice';

type BinaryData = any;

function* fetchLeadsysPictureFiltersSaga(): SagaIterator {
  try {
    const baseApiUrl: string = yield select(selectBaseApiUrl);
    const leadsysPictureFilters: LeadsysPictureFilters = yield call(fetchLeadsysPictureFilters, baseApiUrl);

    yield put(
      leadsysPictureActions.fetchLeadsysPictureFiltersSuccess({
        leadsysPictureFilters: leadsysPictureFilters,
      })
    );
  } catch (error) {
    Sentry.captureException(error);
    yield put(leadsysPictureActions.fetchLeadsysPictureFiltersError());
  }
}

function* fetchLeadsysPicturesSaga({ payload }: PayloadAction<{ filters: LeadsysPictureFilterFormValue; page: number }>): SagaIterator {
  try {
    const baseApiUrl: string = yield select(selectBaseApiUrl);
    const mappedFilters = mapLeadsysPictureFiltersToUrlParams(payload.filters);
    const leadsysPicturesResponse: { leadsysPictures: LeadsysPictureDto[]; pageSize: number; imagesCount: number } = yield call(
      fetchLeadsysPictures,
      baseApiUrl,
      mappedFilters,
      payload.page
    );

    const leadsysPictures = mapLeadsysPictureDtosToLeadsysPictures(leadsysPicturesResponse.leadsysPictures);

    yield put(
      leadsysPictureActions.fetchLeadsysPicturesSuccess({
        leadsysPictures,
        pageSize: leadsysPicturesResponse.pageSize,
        imagesCount: leadsysPicturesResponse.imagesCount,
      })
    );
  } catch (error) {
    Sentry.captureException(error);
    yield put(leadsysPictureActions.fetchLeadsysPicturesError());
  }
}

function* fetchSurveyFiltersSaga({ payload }: PayloadAction<{ typeIds: (string | number)[] }>): SagaIterator {
  try {
    const baseApiUrl: string = yield select(selectBaseApiUrl);
    const surveyFilters: { surveyFilters: LeadsysPictureFilterItem[] } = yield call(fetchSurveyFilters, baseApiUrl, payload.typeIds);

    yield put(leadsysPictureActions.fetchSurveyFiltersSuccess(surveyFilters));
  } catch (error) {
    Sentry.captureException(error);
    yield put(leadsysPictureActions.fetchSurveyFiltersError());
  }
}

function* downloadLeadsysPicturesSaga({ payload }: PayloadAction<{ selectedPictures: LeadsysPicture[] }>): SagaIterator {
  try {
    const baseApiUrl: string = yield select(selectBaseApiUrl);
    const pictureIds = payload.selectedPictures.map((picture: LeadsysPicture) => picture.id);
    const response: BinaryData = yield call(downloadLeadsysPictures, baseApiUrl, pictureIds);

    downloadBinaryData(response, 'application/zip', 'pictures');

    yield put(leadsysPictureActions.downloadLeadsysPicturesSuccess());
  } catch (error) {
    Sentry.captureException(error);
    yield put(leadsysPictureActions.downloadLeadsysPicturesError());
  }
}

export function* leadsysPictureSaga() {
  yield takeLatest(leadsysPictureActions.fetchLeadsysPictureFilters.type, fetchLeadsysPictureFiltersSaga);
  yield takeLatest(leadsysPictureActions.fetchLeadsysPictures.type, fetchLeadsysPicturesSaga);
  yield takeLatest(leadsysPictureActions.fetchSurveyFilters.type, fetchSurveyFiltersSaga);
  yield takeLatest(leadsysPictureActions.downloadLeadsysPictures.type, downloadLeadsysPicturesSaga);
}

export default leadsysPictureSaga;
